import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  LayoutDashboard, BarChart3, TrendingUp, Users, CreditCard, Tags, BookOpen, Sparkles, Coins,
  Network, ShieldCheck, Eye, ClipboardCheck, Heart, Mail, Send, Download, Activity, Inbox, Menu, X, Power, DoorOpen,
} from "lucide-react";
import { api } from "@/lib/api";
import { AdminOverview, AdminEssays, AdminSquare, AdminEmail, AdminCampaigns, AdminAmbassadorInvites } from "./App";
import { AdminAnalytics } from "@/components/AdminAnalytics";
import { AdminAIOrch } from "@/components/AdminAIOrch";
import AdminGovernance from "@/components/AdminGovernance";
import { AdminOntology } from "@/components/AdminOntology";
import { AdminOntologyGovernance } from "@/components/AdminOntologyGovernance";
import { AdminIntelligenceConsole } from "@/components/AdminIntelligenceConsole";
import { AdminExpertReview } from "@/components/AdminExpertReview";
import { AdminAmbassadorMessages } from "@/components/AdminAmbassadorMessages";
import { AdminExport } from "@/components/AdminExport";
import { AdminContinuityPanel } from "@/components/AdminContinuityPanel";
import { AdminContact } from "@/components/AdminContact";
import { AdminMembers } from "@/components/admin/AdminMembers";
import { AdminPlanPricing } from "@/components/admin/AdminPlanPricing";
import { AdminGrowth } from "@/components/admin/AdminGrowth";
import { AdminIpIntegrity } from "@/components/admin/AdminIpIntegrity";
import { AdminMaintenance } from "@/components/admin/AdminMaintenance";
import { AdminSiteStatus } from "@/components/admin/AdminSiteStatus";
import { AdminQaDocuments } from "@/components/admin/AdminQaDocuments";

export const ADMIN_SECTIONS = [
  { group: "Overview & Analytics", items: [
    { key: "overview", label: "Overview", icon: LayoutDashboard, el: <><AdminIpIntegrity compact /><div className="mt-4"><AdminOverview /></div></>, blurb: "Platform vitals at a glance." },
    { key: "growth", label: "Growth & Revenue", icon: TrendingUp, el: <AdminGrowth />, blurb: "Funnel, plan mix, MRR estimate, AI cost vs revenue." },
    { key: "analytics", label: "Visitor Analytics", icon: BarChart3, el: <AdminAnalytics />, blurb: "Acquisition, behaviour, engagement, cohorts." },
  ]},
  { group: "Members", items: [
    { key: "users", label: "Members", icon: Users, el: <AdminMembers />, blurb: "Search, inspect, upgrade, flag or deactivate accounts." },
    { key: "ambassadors", label: "Ambassadors", icon: Heart, el: <AdminAmbassadorMessages />, badge: "ambassador_messages", blurb: "Programme analytics + ambassador messages." },
    { key: "invites", label: "Ambassador Invites", icon: Send, el: <AdminAmbassadorInvites />, blurb: "Mint single-use ambassador invitations." },
    { key: "contact", label: "Contact Inbox", icon: Inbox, el: <AdminContact />, badge: "contact_unread", blurb: "Messages from the contact form." },
  ]},
  { group: "Revenue & Plans", items: [
    { key: "plans", label: "Plans & Pricing", icon: Tags, el: <AdminPlanPricing />, blurb: "Single source of truth for /pricing, /billing, checkout amounts and entitlements." },
    { key: "square", label: "Payments (Square)", icon: CreditCard, el: <AdminSquare />, blurb: "Checkout mode, credentials and SKU amounts." },
    { key: "cost", label: "AI Cost Governance", icon: Coins, el: <AdminGovernance />, blurb: "Burn rate, cost by plan/feature/model, unit weights." },
  ]},
  { group: "Content", items: [
    { key: "essays", label: "Editorial Essays", icon: BookOpen, el: <AdminEssays />, blurb: "Inclusive essay library + identity-aware generation." },
    { key: "campaigns", label: "Email Campaigns", icon: Send, el: <AdminCampaigns />, blurb: "Lifecycle emails and schedules." },
    { key: "email", label: "SendGrid", icon: Mail, el: <AdminEmail />, blurb: "Transport settings + test send." },
  ]},
  { group: "Intelligence & Ontology", items: [
    { key: "ai", label: "AI Orchestration", icon: Sparkles, el: <AdminAIOrch />, blurb: "Models, prompts, versions, analytics." },
    { key: "ontology", label: "Ontology", icon: Network, el: <AdminOntology />, blurb: "41 archetypes, custom archetypes, proposals." },
    { key: "governance", label: "Ontology Governance", icon: ShieldCheck, el: <AdminOntologyGovernance />, badge: "ontology_proposals", blurb: "Versioned ontology, shadow mode, snapshots, match tester." },
    { key: "intel", label: "Member Intelligence", icon: Eye, el: <AdminIntelligenceConsole />, blurb: "Longitudinal memory, events, continuity per member." },
    { key: "expert", label: "Expert Review", icon: ClipboardCheck, el: <AdminExpertReview />, badge: "expert_queue", blurb: "QA queue and structured corrections." },
  ]},
  { group: "Operations", items: [
    { key: "export", label: "IP Export", icon: Download, el: <AdminExport />, blurb: "Scoped export keys, previews, IP binder." },
    { key: "site-status", label: "Site status", icon: DoorOpen, el: <AdminSiteStatus />, blurb: "Block or allow new account sign-ups before launch; ambassador links and admin-created accounts keep working." },
    { key: "maintenance", label: "Maintenance mode", icon: Power, el: <AdminMaintenance />, blurb: "Lock the site to admins before a deploy; members get a countdown and are signed out safely." },
    { key: "continuity", label: "Continuity & IP", icon: Activity, el: <><AdminIpIntegrity /><div className="mt-5"><AdminContinuityPanel /></div><div className="mt-5"><AdminQaDocuments /></div></>, blurb: "Live local-IP store probe + guardrail status." },
  ]},
];

const ALL_ITEMS = ADMIN_SECTIONS.flatMap((g) => g.items);

export default function AdminConsole() {
  const { section } = useParams();
  const nav = useNavigate();
  const active = ALL_ITEMS.find((i) => i.key === section) || ALL_ITEMS[0];
  const [badges, setBadges] = useState({});
  const [open, setOpen] = useState(false);
  const totalBadges = ALL_ITEMS.reduce((n, i) => n + (i.badge ? badges[i.badge] || 0 : 0), 0);

  useEffect(() => {
    if (!section) nav("/admin-ss/overview", { replace: true });
    setOpen(false); // route changed → drawer always closes
  }, [section, nav]);

  // Plain drawer (no portal / body pointer-events juggling — those got stuck on iOS Safari).
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = prev; window.removeEventListener("keydown", onKey); };
  }, [open]);

  useEffect(() => {
    const load = () => api.get("/admin/badges").then((r) => setBadges(r.data || {})).catch(() => {});
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const NavList = () => (
    <nav className="space-y-5" data-testid="admin-sidebar">
      {ADMIN_SECTIONS.map((g) => (
        <div key={g.group}>
          <p className="px-3 text-[10px] tracking-[0.35em] uppercase text-cocoa/45">{g.group}</p>
          <ul className="mt-1.5 space-y-0.5">
            {g.items.map((i) => {
              const isActive = i.key === active.key;
              const count = i.badge ? badges[i.badge] : 0;
              return (
                <li key={i.key}>
                  <Link to={`/admin-ss/${i.key}`} data-testid={`admin-tab-${i.key}`} onClick={() => setOpen(false)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${isActive ? "bg-cocoa text-ivory" : "text-cocoa/75 hover:bg-champagne/70 hover:text-cocoa"}`}>
                    <i.icon size={15} className={isActive ? "text-gold" : "text-cocoa/50"} />
                    <span className="flex-1 truncate">{i.label}</span>
                    {count > 0 && (
                      <span data-testid={`admin-badge-${i.key}`} className={`min-w-[1.4rem] h-5 px-1.5 rounded-full text-[11px] font-semibold flex items-center justify-center ${isActive ? "bg-gold text-cocoa" : "bg-rose/90 text-ivory"}`}>{count}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <div data-testid="admin-page" className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-[11px] tracking-[0.4em] uppercase text-mauve">Admin</p>
          <h1 className="font-heading text-3xl sm:text-4xl text-cocoa">SugarSense control room.</h1>
          <p className="mt-1 text-cocoa/65 text-sm hidden sm:block">Private member content stays out of these screens — only system signals, entitlements and integrations — except the audited “Member inputs” view under Member Intelligence.</p>
        </div>
      </div>

      {/* Mobile / tablet: sticky section switcher (always reachable while scrolling) + slide-in drawer */}
      <div className="lg:hidden sticky top-[64px] z-30 -mx-4 px-4 py-2 mt-4 bg-ivory/90 backdrop-blur-md" data-testid="admin-mobile-bar">
        <button type="button" onClick={() => setOpen(true)} data-testid="admin-nav-toggle" aria-label="Choose admin section" aria-haspopup="dialog" aria-expanded={open}
          className="w-full ss-card px-4 py-3 flex items-center gap-3 text-left active:scale-[0.99] transition-transform">
          <span className="w-9 h-9 rounded-full bg-cocoa text-gold flex items-center justify-center shrink-0"><active.icon size={16} /></span>
          <span className="flex-1 min-w-0">
            <span className="block text-[10px] tracking-[0.3em] uppercase text-cocoa/45">Section</span>
            <span className="block font-heading text-lg text-cocoa truncate">{active.label}</span>
          </span>
          {totalBadges > 0 && <span className="min-w-[1.4rem] h-5 px-1.5 rounded-full text-[11px] font-semibold bg-rose/90 text-ivory flex items-center justify-center" data-testid="admin-nav-toggle-badge">{totalBadges}</span>}
          <Menu size={18} className="text-cocoa/60" />
        </button>
      </div>
      {open && createPortal(
        <div className="lg:hidden fixed inset-0 z-[70]" role="dialog" aria-modal="true" aria-label="Admin sections" data-testid="admin-nav-drawer">
          <button type="button" aria-label="Close menu" onClick={() => setOpen(false)} data-testid="admin-nav-drawer-backdrop"
            className="absolute inset-0 bg-cocoa/55 backdrop-blur-[2px]" />
          <div className="absolute inset-y-0 left-0 w-[88vw] max-w-sm bg-ivory shadow-2xl overflow-y-auto animate-in slide-in-from-left duration-300">
            <div className="px-5 pt-5 pb-3 flex items-start justify-between gap-3">
              <div>
                <p className="font-heading text-2xl text-cocoa">Control room</p>
                <p className="text-xs text-cocoa/55">Pick a section. Counts show items waiting on you.</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" data-testid="admin-nav-drawer-close" className="w-9 h-9 rounded-full border border-champagne flex items-center justify-center text-cocoa/70"><X size={16} /></button>
            </div>
            <div className="px-3 pb-8"><NavList /></div>
          </div>
        </div>,
        document.body
      )}

      <div className="mt-6 grid lg:grid-cols-[240px_1fr] gap-6">
        <aside className="hidden lg:block ss-card p-3 lg:sticky lg:top-24 self-start max-h-[80vh] overflow-y-auto">
          <NavList />
        </aside>
        <section className="min-w-0" data-testid={`admin-section-${active.key}`}>
          <div className="hidden lg:flex items-center gap-2 text-cocoa">
            <active.icon size={18} className="text-gold" />
            <h2 className="font-heading text-2xl">{active.label}</h2>
          </div>
          <p className="mt-0.5 text-sm text-cocoa/55">{active.blurb}</p>
          <div className="mt-5">{active.el}</div>
        </section>
      </div>
    </div>
  );
}
